import { useSyncExternalStore } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Colors } from "@/constants/theme";
import type { Theme } from "@/constants/theme";

export type ThemeMode = "light" | "dark";

/**
 * Dark instrument (founder, 2026-10-02): the app is dark everywhere. The V7
 * tab screens style from STATIC tokens that cannot follow a toggle, so a light
 * choice would leave the app half paper, half navy. Light stays in the code —
 * flip LIGHT_MODE_AVAILABLE to bring the Settings switch back once the V7
 * screens read the theme at render time.
 *
 * The storage key moved (`_v2`) so a persisted "light" from the paper era does
 * not override the new default.
 */
export const LIGHT_MODE_AVAILABLE = false;
const STORAGE_KEY = "fahali_theme_mode_v2";

let _mode: ThemeMode = "dark";
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function setThemeMode(mode: ThemeMode) {
  if (!LIGHT_MODE_AVAILABLE && mode === "light") return;
  if (mode === _mode) return;
  _mode = mode;
  emit();
  AsyncStorage.setItem(STORAGE_KEY, mode).catch(() => {});
}

export function toggleThemeMode() {
  setThemeMode(_mode === "dark" ? "light" : "dark");
}

// Hydrate the persisted choice once on module load.
AsyncStorage.getItem(STORAGE_KEY)
  .then((v) => {
    if (!LIGHT_MODE_AVAILABLE) return;
    if ((v === "light" || v === "dark") && v !== _mode) {
      _mode = v;
      emit();
    }
  })
  .catch(() => {});

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function getSnapshot(): ThemeMode {
  return _mode;
}

export function useTheme(): {
  theme: Theme;
  isDark: boolean;
  mode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (m: ThemeMode) => void;
} {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const theme = Colors[mode] as Theme;
  return { theme, isDark: mode === "dark", mode, toggleTheme: toggleThemeMode, setThemeMode };
}
